import type { ProcessedPart } from '../utils/message-processing';
import { getSkillDisplayNameFromPath } from './ToolCallsRenderers/skill-display';
import {
  getProgressEventsFromPart,
  getReadPathFromPayload,
  getToolNameFromPart,
  getToolPayloadFromPart,
  isCodeAnalyzerToolPart,
  isObjectResultPart,
  isOmittedNestedAnalyzerPart,
  isPendingHITLPart,
  isRunningPart,
} from './chat-message-part-accessors';
import type {
  ChatRenderItem,
  ChatRenderUnit,
  RenderablePart,
} from './chat-message-list-types';
import { getToolConfig } from './tool-config';
import {
  headlineKindForSurface,
  type ChatHeadlineKind,
  type ChatHeadlineSurface,
} from './chat-headline-actions';

export type ConsolidationSurface = ChatHeadlineSurface;

type ToolLane = {
  noun?: string;
  surface?: ConsolidationSurface;
  innerClaude?: boolean;
};

const TOOL_LANE: Record<string, ToolLane> = {
  'websearch-tool': { noun: 'web search', surface: 'search-web' },
  'web-search-tool': { surface: 'search-web' },
  'search-tracked-web-pages': { noun: 'web page', surface: 'explored' },
  'get-tracked-web-page': { noun: 'web page', surface: 'explored' },
  'web-page-details': { noun: 'web page', surface: 'explored' },
  'web-page-read': { noun: 'web page', surface: 'explored' },
  'read-web-page': { noun: 'web page', surface: 'explored' },
  'list-web-pages-for-endpoint': { noun: 'web page', surface: 'explored' },
  'search-tracked-endpoints': { noun: 'endpoint', surface: 'explored' },
  'get-tracked-endpoint-schema': { noun: 'endpoint', surface: 'explored' },
  'endpoint-details-tool': { noun: 'endpoint', surface: 'explored' },
  'endpoint-details': { noun: 'endpoint', surface: 'explored' },
  'endpoint-read': { noun: 'endpoint', surface: 'explored' },
  'get-endpoint-details': { noun: 'endpoint', surface: 'explored' },
  'read-endpoint-details': { noun: 'endpoint', surface: 'explored' },
  'read-endpoint-details-tool': { noun: 'endpoint', surface: 'explored' },
  'endpoints-explorer-tool': { noun: 'endpoint', surface: 'explored' },
  'list-endpoints-on-web-page': { noun: 'endpoint', surface: 'explored' },
  'feature-analysis-tool': { noun: 'feature', surface: 'explored' },
  'har-entries-tool': { noun: 'network log', surface: 'explored' },
  'har-explorer-tool': { noun: 'network log', surface: 'explored' },
  'list-assets-tool': { noun: 'asset', surface: 'explored' },
  'mcp--terra--terra-query': { noun: 'data query', surface: 'explored' },
  'mcp--terra--terra-query-validate': {
    noun: 'query check',
    surface: 'explored',
  },
  'mcp--terra--terra-query-schema': {
    noun: 'data catalog',
    surface: 'explored',
  },
  'docs-search-tool': { noun: 'doc', surface: 'explored' },
  'search-existing-skills': { noun: 'skill', surface: 'used-skill' },
  'get-memory-tool': { noun: 'memory', surface: 'recall' },
  'get-neo-memories': { noun: 'memory', surface: 'recall' },
  'long-term-memory-tool': { noun: 'memory', surface: 'recall' },
  'signals-explorer-tool': { noun: 'signal', surface: 'explored' },
  'signals-semantic-search-tool': { noun: 'signal', surface: 'explored' },
  'findings-explorer-tool': { noun: 'finding', surface: 'explored' },
  'finding-semantic-search-tool': { noun: 'finding', surface: 'explored' },
  'findings-semantic-search-tool': { noun: 'finding', surface: 'explored' },
  'findings-tool': { noun: 'finding', surface: 'explored' },
  'executions-explorer-tool': { noun: 'execution', surface: 'explored' },
  ls: { noun: 'file', surface: 'analyze-source', innerClaude: true },
  read: { noun: 'file', surface: 'analyze-source', innerClaude: true },
  glob: { noun: 'file', surface: 'analyze-source', innerClaude: true },
  write: { noun: 'file', surface: 'analyze-source', innerClaude: true },
  edit: { noun: 'file', surface: 'analyze-source', innerClaude: true },
  grep: { noun: 'search', surface: 'analyze-source', innerClaude: true },
  bash: { noun: 'command', surface: 'analyze-source', innerClaude: true },
  'code-analyzer-tool': { noun: 'analysis', surface: 'analyze-source' },
  'code-analyzer': { noun: 'analysis', surface: 'analyze-source' },
};

const SKILL_CONSOLIDATABLE_NOUN = 'skill';

const RUNNING_CLAUDE_TOOL_STAGE_PATTERN = /^Running\s+(\S+)/i;

const ALWAYS_WRAP_SURFACES = new Set<ConsolidationSurface>([
  'used-skill',
  'explored',
  'search-web',
  'recall',
]);

function normalizeToolName(toolName: string): string {
  return toolName.replace(/_/g, '-').toLowerCase();
}

function getSkillSlugFromPart(processedPart: ProcessedPart): string | null {
  return getSkillDisplayNameFromPath(
    getReadPathFromPayload(getToolPayloadFromPart(processedPart)),
  );
}

function isSkillReadPart(processedPart: ProcessedPart): boolean {
  const toolName = getToolNameFromPart(processedPart);

  if (!toolName) {
    return false;
  }

  const normalized = normalizeToolName(toolName);

  if (normalized !== 'read-file' && normalized !== 'read') {
    return false;
  }

  const path = getReadPathFromPayload(getToolPayloadFromPart(processedPart));

  return (
    getSkillDisplayNameFromPath(path) !== null || /\/skill\.md$/i.test(path)
  );
}

function getConsolidationLane(
  processedPart: ProcessedPart,
): ConsolidationSurface | undefined {
  if (processedPart.kind !== 'data-message') {
    return undefined;
  }

  if (isPendingHITLPart(processedPart) || isObjectResultPart(processedPart)) {
    return undefined;
  }

  if (isSkillReadPart(processedPart)) {
    return 'used-skill';
  }

  const toolName = getToolNameFromPart(processedPart);

  if (!toolName) {
    return undefined;
  }

  const normalized = normalizeToolName(toolName);

  return TOOL_LANE[normalized]?.surface;
}

export function getHeadlineKindForPart(
  processedPart: ProcessedPart,
): ChatHeadlineKind {
  const lane = getConsolidationLane(processedPart);

  return lane ? headlineKindForSurface(lane) : 'passthrough';
}

export function isInnerClaudeActivityPart(
  processedPart: ProcessedPart,
): boolean {
  const toolName = getToolNameFromPart(processedPart);

  if (!toolName) {
    return false;
  }

  return TOOL_LANE[normalizeToolName(toolName)]?.innerClaude === true;
}

function nounFromClaudeProgressStage(stage: string): string | undefined {
  const match = RUNNING_CLAUDE_TOOL_STAGE_PATTERN.exec(stage);

  if (!match) {
    return undefined;
  }

  return TOOL_LANE[normalizeToolName(match[1])]?.noun;
}

function getConsolidatableNoun(
  processedPart: ProcessedPart,
): string | undefined {
  if (isSkillReadPart(processedPart)) {
    return SKILL_CONSOLIDATABLE_NOUN;
  }

  const toolName = getToolNameFromPart(processedPart);

  if (!toolName) {
    return undefined;
  }

  return TOOL_LANE[normalizeToolName(toolName)]?.noun;
}

export function isConsolidatablePart(processedPart: ProcessedPart): boolean {
  return getConsolidationLane(processedPart) !== undefined;
}

function pluralizeNoun(noun: string, count: number): string {
  if (count === 1) {
    return noun;
  }

  if (noun.endsWith('sis')) {
    return `${noun.slice(0, -2)}es`;
  }

  if (/(s|sh|ch|x|z)$/.test(noun)) {
    return `${noun}es`;
  }

  if (/[^aeiou]y$/.test(noun)) {
    return `${noun.slice(0, -1)}ies`;
  }

  return `${noun}s`;
}

function buildCountedSummary(items: RenderablePart[]): string | undefined {
  const counts = new Map<string, number>();
  const order: string[] = [];

  for (const { processedPart } of items) {
    addCountedNoun({
      counts,
      order,
      noun: getConsolidatableNoun(processedPart) ?? 'action',
    });
  }

  return formatCountedNouns({ counts, order });
}

export function buildConsolidatedSummary(
  items: RenderablePart[],
  _surface: ConsolidationSurface = 'explored',
): string | undefined {
  return buildCountedSummary(items);
}

function addCountedNoun({
  counts,
  order,
  noun,
}: {
  counts: Map<string, number>;
  order: string[];
  noun: string;
}): void {
  if (!counts.has(noun)) {
    order.push(noun);
  }

  counts.set(noun, (counts.get(noun) ?? 0) + 1);
}

function formatCountedNouns({
  counts,
  order,
}: {
  counts: Map<string, number>;
  order: string[];
}): string | undefined {
  if (order.length === 0) {
    return undefined;
  }

  return order
    .map((noun) => {
      const count = counts.get(noun) ?? 0;

      return `${count} ${pluralizeNoun(noun, count)}`;
    })
    .join(', ');
}

export function buildActivityCountSummary(
  children: RenderablePart[],
): string | undefined {
  const counts = new Map<string, number>();
  const order: string[] = [];
  const hasInnerClaudeActivity = children.some((item) =>
    isInnerClaudeActivityPart(item.processedPart),
  );

  for (const { processedPart } of children) {
    if (
      processedPart.kind === 'assistant-text' &&
      processedPart.part.type === 'reasoning'
    ) {
      continue;
    }

    if (processedPart.kind !== 'data-message') {
      continue;
    }

    if (isSkillReadPart(processedPart)) {
      continue;
    }

    if (isCodeAnalyzerToolPart(processedPart)) {
      for (const event of getProgressEventsFromPart(processedPart)) {
        if (event.stage === 'Thinking' || event.stage === 'Summarizing') {
          continue;
        }

        if (!hasInnerClaudeActivity) {
          const noun = nounFromClaudeProgressStage(event.stage);

          if (noun) {
            addCountedNoun({ counts, order, noun });
          }
        }
      }
    } else {
      const toolName = getToolNameFromPart(processedPart);
      const config = toolName ? getToolConfig(toolName) : undefined;
      const hideFromSummary =
        config !== undefined && config.hideFromSubAgentSummary === true;

      if (!hideFromSummary) {
        const noun =
          getConsolidatableNoun(processedPart) ??
          config?.name.toLowerCase() ??
          'action';

        addCountedNoun({ counts, order, noun });
      }
    }
  }

  return formatCountedNouns({ counts, order });
}

export { isOmittedNestedAnalyzerPart };

export function getConsolidatedLiveLabel(
  items: RenderablePart[],
): string | undefined {
  for (let index = items.length - 1; index >= 0; index--) {
    const { processedPart } = items[index];

    if (!isRunningPart(processedPart)) {
      continue;
    }

    if (isSkillReadPart(processedPart)) {
      const skillName = getSkillSlugFromPart(processedPart);

      return skillName ? `Reading skill ${skillName}` : 'Reading skill';
    }

    const toolName = getToolNameFromPart(processedPart);
    const config = toolName ? getToolConfig(toolName) : undefined;

    return config?.runningText ?? config?.name;
  }

  return undefined;
}

function shouldWrapConsolidatedRun({
  run,
  surface,
}: {
  run: RenderablePart[];
  surface: ConsolidationSurface;
}): boolean {
  if (ALWAYS_WRAP_SURFACES.has(surface)) {
    return run.length >= 1;
  }

  return run.length >= 2;
}

function flushConsolidatedRun({
  run,
  surface,
  onConsolidated,
  onSingle,
}: {
  run: RenderablePart[];
  surface: ConsolidationSurface | undefined;
  onConsolidated: (args: {
    items: RenderablePart[];
    key: string;
    surface: ConsolidationSurface;
  }) => void;
  onSingle: (item: RenderablePart) => void;
}): void {
  if (run.length === 0 || !surface) {
    return;
  }

  if (shouldWrapConsolidatedRun({ run, surface })) {
    onConsolidated({
      items: run,
      key: `consolidated-${run[0].processedPart.key}`,
      surface,
    });

    return;
  }

  onSingle(run[0]);
}

export function partitionConsolidatableParts(
  parts: RenderablePart[],
): ChatRenderUnit[] {
  const units: ChatRenderUnit[] = [];
  let run: RenderablePart[] = [];
  let runLane: ConsolidationSurface | undefined;

  const flushRun = () => {
    flushConsolidatedRun({
      run,
      surface: runLane,
      onConsolidated: ({ items, key, surface }) => {
        units.push({ type: 'consolidated', items, key, surface });
      },
      onSingle: (item) => {
        units.push({ type: 'single', item });
      },
    });
    run = [];
    runLane = undefined;
  };

  for (const item of parts) {
    const lane = getConsolidationLane(item.processedPart);

    if (lane && lane === runLane) {
      run.push(item);
      continue;
    }

    flushRun();

    if (lane) {
      runLane = lane;
      run.push(item);
      continue;
    }

    units.push({ type: 'single', item });
  }

  flushRun();

  return units;
}

export function consolidateReadSearchRenderItems(
  items: ChatRenderItem[],
): ChatRenderItem[] {
  const result: ChatRenderItem[] = [];
  let run: RenderablePart[] = [];
  let runLane: ConsolidationSurface | undefined;

  const flushRun = () => {
    flushConsolidatedRun({
      run,
      surface: runLane,
      onConsolidated: ({ items: runItems, key, surface }) => {
        result.push({
          kind: 'consolidated-group',
          items: runItems,
          key,
          surface,
        });
      },
      onSingle: (item) => {
        result.push({ kind: 'part', item });
      },
    });
    run = [];
    runLane = undefined;
  };

  for (const item of items) {
    const lane =
      item.kind === 'part'
        ? getConsolidationLane(item.item.processedPart)
        : undefined;

    if (lane && item.kind === 'part' && lane === runLane) {
      run.push(item.item);
      continue;
    }

    flushRun();

    if (lane && item.kind === 'part') {
      runLane = lane;
      run.push(item.item);
      continue;
    }

    result.push(item);
  }

  flushRun();

  return result;
}
